import {useEffect, useMemo, useRef, useState} from 'react';
import {
  RELATED_PRODUCTS_TAB_KEY_BUDGET_FRIENDLY,
  RELATED_PRODUCTS_TAB_KEY_MORE_FITS_AND_SIZES,
  RELATED_PRODUCTS_TAB_KEY_MORE_FROM_THIS_BRAND,
  RELATED_PRODUCTS_TAB_KEY_YOU_MAY_ALSO_LIKE,
  RELATED_PRODUCTS_TABS
} from 'constants/pdpRelatedProducts';
import isAlgoliaRecommendations from 'utils/isAlgoliaRecommendations';
import {
  EVENT_TYPE,
  fireTabbedViewEvent
} from 'utils/metrics/pdpRelatedStylesTabbedView';

const filterByCategory = (products, categoryId) => {
  if (!products || !categoryId) return products;
  return products.filter(
    (product) => product.primary_category_id === categoryId
  );
};

const filterByBrand = (products, brand) => {
  if (!products) return products;
  if (!brand || brand === '') return [];
  return products.filter((item) =>
    item.brand?.find(
      (recoBrand) =>
        recoBrand !== '' && recoBrand.toLowerCase() === brand.toLowerCase()
    )
  );
};

const getMinPrice = (product) => {
  return product.default_unit_prices.reduce((min, value) =>
    value.price < min.price ? value : min
  );
};

export default function useRelatedProductsTabbedView({
  alternativeProducts,
  brand,
  breakpoint,
  isApparel,
  isRecommendationsProductsFetched,
  isRecommendationsProductsRefetching,
  isRelatedProductsFetched,
  isRelatedProductsRefetching,
  primaryCategoryId,
  recommendations,
  relatedProducts
}) {
  const loadedEventFired = useRef(false);

  const [relatedProductsActiveTab, setRelatedProductsActiveTab] = useState(
    RELATED_PRODUCTS_TAB_KEY_YOU_MAY_ALSO_LIKE
  );

  const shouldTabbedViewRender =
    breakpoint !== 'xs' && breakpoint !== 'sm' && isAlgoliaRecommendations();

  const tabbedViewIsLoading = useMemo(() => {
    switch (relatedProductsActiveTab) {
      case RELATED_PRODUCTS_TAB_KEY_MORE_FITS_AND_SIZES:
        return !isRelatedProductsFetched || isRelatedProductsRefetching;
      default:
        return (
          !isRecommendationsProductsFetched ||
          isRecommendationsProductsRefetching
        );
    }
  }, [
    relatedProductsActiveTab,
    isRecommendationsProductsFetched,
    isRecommendationsProductsRefetching,
    isRelatedProductsFetched,
    isRelatedProductsRefetching
  ]);

  const tabbedViewProducts = useMemo(() => {
    if (!shouldTabbedViewRender) {
      return alternativeProducts.slice(0, 4);
    }

    let products;
    switch (relatedProductsActiveTab) {
      case RELATED_PRODUCTS_TAB_KEY_MORE_FITS_AND_SIZES:
        products = relatedProducts?.complements;
        break;
      case RELATED_PRODUCTS_TAB_KEY_BUDGET_FRIENDLY:
        products = filterByCategory(
          recommendations?.related_products,
          primaryCategoryId
        );
        if (products) {
          products = [...products].sort(
            (a, b) => getMinPrice(a).price - getMinPrice(b).price
          );
        }
        break;
      case RELATED_PRODUCTS_TAB_KEY_MORE_FROM_THIS_BRAND:
        products = filterByCategory(
          recommendations?.related_products,
          primaryCategoryId
        );
        products = filterByBrand(products, brand);
        break;
      default:
        products = filterByCategory(
          recommendations?.related_products,
          primaryCategoryId
        );
    }
    return products;
  }, [
    relatedProductsActiveTab,
    recommendations,
    relatedProducts,
    shouldTabbedViewRender,
    alternativeProducts,
    brand,
    primaryCategoryId
  ]);

  const tabsToShow = useMemo(
    () =>
      Object.values(RELATED_PRODUCTS_TABS).filter(({value}) => {
        if (value === RELATED_PRODUCTS_TAB_KEY_YOU_MAY_ALSO_LIKE) {
          return (
            !isRecommendationsProductsFetched ||
            recommendations?.related_products?.length > 0
          );
        }
        if (value === RELATED_PRODUCTS_TAB_KEY_MORE_FITS_AND_SIZES) {
          if (isApparel) {
            return (
              !isRelatedProductsFetched ||
              relatedProducts?.complements?.length > 0
            );
          }
          return false;
        }
        if (value === RELATED_PRODUCTS_TAB_KEY_MORE_FROM_THIS_BRAND) {
          const filteredProducts = filterByCategory(
            recommendations?.related_products,
            primaryCategoryId
          );
          return filterByBrand(filteredProducts, brand)?.length > 0;
        }
        const filteredProducts = filterByCategory(
          recommendations?.related_products,
          primaryCategoryId
        );
        return (
          !isRecommendationsProductsFetched || filteredProducts?.length > 0
        );
      }),
    [
      brand,
      isApparel,
      isRecommendationsProductsFetched,
      recommendations,
      isRelatedProductsFetched,
      relatedProducts,
      primaryCategoryId
    ]
  );

  const tabOrder = useMemo(() => {
    return [
      RELATED_PRODUCTS_TAB_KEY_YOU_MAY_ALSO_LIKE,
      ...[
        RELATED_PRODUCTS_TAB_KEY_BUDGET_FRIENDLY,
        RELATED_PRODUCTS_TAB_KEY_MORE_FROM_THIS_BRAND,
        RELATED_PRODUCTS_TAB_KEY_MORE_FITS_AND_SIZES
      ].sort(() => Math.random() - 0.5)
    ];
  }, []);

  const tabs = useMemo(() => {
    return tabsToShow.sort((a, b) => {
      return tabOrder.indexOf(a.value) - tabOrder.indexOf(b.value);
    });
  }, [tabsToShow]);

  useEffect(() => {
    if (tabs.length > 0) {
      const activeTabExists = tabs.some(
        (tab) => tab.value === relatedProductsActiveTab
      );
      if (!activeTabExists) {
        setRelatedProductsActiveTab(tabs[0].value);
      }
    }
  }, [tabs, relatedProductsActiveTab]);

  useEffect(() => {
    if (
      !loadedEventFired.current &&
      isRelatedProductsFetched &&
      isRecommendationsProductsFetched
    ) {
      loadedEventFired.current = true;
      fireTabbedViewEvent({
        availableTabs: tabsToShow.map((tab) => tab.value),
        eventType: EVENT_TYPE.LOADED
      });
    }
  }, [
    isRelatedProductsFetched,
    isRecommendationsProductsFetched,
    loadedEventFired.current,
    tabsToShow
  ]);

  const hasAnyData = useMemo(() => {
    return tabs.length > 0;
  }, [tabs.length]);

  return {
    hasAnyData,
    relatedProductsActiveTab,
    setRelatedProductsActiveTab,
    shouldTabbedViewRender,
    tabbedViewIsLoading,
    tabbedViewProducts,
    tabs
  };
}
